/*
 * StackHeadline
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { key } from 'styled-theme';
import { ifProp } from 'styled-tools';

import { Tooltip } from '~/components';
// $FlowIssue
import { ReactComponent as IconInfo } from '@/icons/ic-info.svg';

export type StackHeadlineProps = {
  icon: React$Node,
  title: string,
  tooltip: React$Node,
  variant?: string,
  isIconVisible?: boolean,
  onClick?: () => void,
};

const foregroundColor = ({ variant }) => key(`flasks.${variant}[0]`);

const StackHeadlineWrapper = styled.div`
  display: flex;
  width: 160px;
  box-sizing: border-box;
  color: ${foregroundColor};
  flex-direction: column;
`;

const StackHeadlineTitleWrapper = styled.div.attrs(() => ({
  className: 'react-stack-headline-title',
}))`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StackHeadlineTitle = styled.h2`
  margin-right: 10px;
  color: ${foregroundColor};
  line-height: normal;
`;

const StackHeadlineInfoIcon = styled(IconInfo)`
  display: block;
  margin-bottom: 4px;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  opacity: 0.5;
`;

const StackHeadlineIcon = styled.span`
  ${ifProp(
    'isClickable',
    css`
      cursor: pointer;
    `,
  )}
`;

/**
 * StackHeadline React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Node<*>} [props.icon] - icon
 * @param {string} [props.title] - title
 * @param {string} [props.tooltip] - tooltip
 * @param {string} [props.variant] - flask style variant
 * @param {boolean} [props.isIconVisible] - icon visibility flag
 * @param {Function} [props.onClick] - icon click handler
 * @example <caption>Simple usage</caption>
 * <StackHeadline />
 * @returns {React.Node<*>} React-component
 */
export const StackHeadline = ({
  icon,
  title,
  tooltip,
  variant,
  isIconVisible,
  onClick,
}: StackHeadlineProps) => (
  <StackHeadlineWrapper
    variant={variant}
    className="react-stack-headline"
    testid="stack-headline"
  >
    {tooltip && (
      <Tooltip
        placement="bottomLeft"
        overlay={tooltip}
        align={{ offset: [-14, 0] }}
      >
        <StackHeadlineInfoIcon />
      </Tooltip>
    )}
    <StackHeadlineTitleWrapper>
      <StackHeadlineTitle variant={variant}>{title}</StackHeadlineTitle>
      {isIconVisible && (
        <StackHeadlineIcon isClickable={!!onClick} onClick={onClick}>
          {icon}
        </StackHeadlineIcon>
      )}
    </StackHeadlineTitleWrapper>
  </StackHeadlineWrapper>
);

StackHeadline.defaultProps = {
  variant: 'one',
  isIconVisible: true,
};
